import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { reactToLetter } from "@/lib/api";

export const HEART_REACTIONS = [
  { id: "touched", emoji: "❤️", label: "This touched my heart", accent: "#991B1B" },
  { id: "smile", emoji: "🥹", label: "I couldn't stop smiling", accent: "#D97706" },
  { id: "cry", emoji: "😭", label: "It made me cry", accent: "#3730A3" },
  { id: "hug", emoji: "🤗", label: "I felt your hug", accent: "#D97757" },
  { id: "treasure", emoji: "🌸", label: "I'll treasure this forever", accent: "#DB2777" },
  { id: "thank_you", emoji: "✨", label: "Thank you for this memory", accent: "#9333EA" },
];

export default function HeartReactions({ slug, onReacted }) {
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const pick = async (r) => {
    if (selected || submitting) return;
    setSubmitting(true);
    try {
      await reactToLetter(slug, r.id);
      setSelected(r);
      onReacted && onReacted(r.id);
    } catch {
      toast.error("Could not send your reaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-16" data-testid="heart-reactions">
      <div className="text-center">
        <p className="uppercase tracking-[0.35em] text-xs text-[#991B1B]">A quiet reply</p>
        <h3 className="mt-2 font-serif-elegant text-3xl md:text-4xl text-[#1C1917]">
          How did this make you feel?
        </h3>
        <p className="mt-2 text-sm text-[#4c3f34]/80 max-w-md mx-auto">
          Pick one — it is sent quietly and only the person who wrote to you will see it.
        </p>
      </div>

      <AnimatePresence mode="wait">
        {!selected ? (
          <motion.div
            key="picker"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto"
          >
            {HEART_REACTIONS.map((r) => (
              <motion.button
                key={r.id}
                data-testid={`reaction-${r.id}`}
                onClick={() => pick(r)}
                disabled={submitting}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.97 }}
                className="group text-left rounded-[6px] bg-[#FDFBF7] border border-[#1C1917]/10 hover:border-[#991B1B]/40 p-5 flex items-center gap-4 transition-all disabled:opacity-60 shadow-sm"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-2xl shrink-0"
                  style={{ background: `${r.accent}18`, color: r.accent }}
                >
                  {r.emoji}
                </div>
                <div className="font-serif-elegant text-lg md:text-xl text-[#1C1917] leading-tight">
                  {r.label}
                </div>
              </motion.button>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-10 text-center"
            data-testid="reaction-thanks"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="mx-auto w-20 h-20 rounded-full flex items-center justify-center text-4xl"
              style={{ background: `${selected.accent}22`, color: selected.accent }}
            >
              {selected.emoji}
            </motion.div>
            <p className="mt-6 font-serif-elegant text-2xl md:text-3xl text-[#1C1917]">
              {selected.label}
            </p>
            <p className="mt-3 font-hand text-2xl text-[#991B1B]">— quietly sent</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
